// src/components/resources/tools/generators/BuyerPersona/PersonaPreview.tsx

/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */

import { formatTitleCase } from '~/lib/utils/formatters'
import { BrandLoyaltyHeartIcon, ChallengesIcon, CommunicationIcon, FacebookIcon, GiftIcon, GoalsIcon, GoogleIcon, SalePromoIcon, ScalesOfJusticeIcon, ShoppingBasketIcon, TikTokIcon, VIPIcon, LinkedInIcon, UserTargetingIcon, LookalikeTargetingIcon, KPIsIcon, TestingFlaskIcon, BudgetCircleIcon, OptimizationFlashlightIcon, SuccessIcon, OptimizationSparkleIcon, FeedbackChatBubbleIcon, StarIcon, ShoppingCartIcon, ClockIcon, ClipboardIcon, SearchIcon, UserIcon, HeartIcon, GraduationCapIcon } from './Icons'
import { usePersona } from './PersonaContext'
import { clsx } from 'clsx'
import type { PersonaData } from './PersonaContext'
import { getPlatformIcon, getChannelIcon, getDeviceEmoji, getContentEmoji, getDigitalInfluenceIcon } from './personaUtils'

interface PersonaPreviewProps {
  stepId: string
}

/**
 * Helper function to truncate long text for preview displays
 * @param {string} text The text to truncate
 * @param {number} maxLength Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
function truncateText(text: string | undefined, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

export function PersonaPreview({ stepId }: PersonaPreviewProps): JSX.Element {
  const { formData } = usePersona()
  
  // Type the form data properly
  // eslint-disable-next-line
  const data = formData as PersonaData
  
  // Define max lengths for different sections
  const PSYCHOGRAPHICS_MAX_LENGTH = 150
  const ONLINE_BEHAVIOR_MAX_LENGTH = 200
  const TARGETING_MAX_LENGTH = 250
  
  // Business context preview
  if (stepId === 'business-context') {
    return (
      <div className="business-context-preview p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold mb-3 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {data.businessType ? `Creating a ${formatTitleCase(data.businessType)} Persona` : 'Select Your Business Type'}
          </h3>
          
          {data.companyName && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              For: <span className="font-medium">{data.companyName}</span>
            </p>
          )}
        </div>
      </div>
    )
  }
  
  // Basic information preview
  if (stepId === 'basic-info') {
    // Determine business type context
    const isB2BG = data.businessType?.toLowerCase().includes('b2b') || data.businessType?.toLowerCase().includes('b2g') || false
    // B2BG includes B2B and B2G, grouping them together for the purposes of determining appropriate questions/fields
    // B2C includes B2C, DTC, Marketplace or anything not B2BG - we do not need to check for this as we will use the !isB2BG variable
    
    return (
      <div className="persona-preview-card p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-start">
          {/* Avatar placeholder */}
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xl font-bold mr-4 shadow-sm">
            {typeof data.personaName === 'string' && data.personaName 
              ? data.personaName.charAt(0).toUpperCase() 
              : (typeof data.name === 'string' && data.name 
                ? data.name.charAt(0).toUpperCase() 
                : '?')}
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              {typeof data.personaName === 'string' && data.personaName 
                ? data.personaName 
                : (typeof data.name === 'string' && data.name 
                  ? data.name 
                  : 'Unnamed Persona')}
            </h3>
            
            {/* Show job title based on business type (B2B/B2G vs B2C/D2C/Marketplace) */}
            {((isB2BG 
              ? data.jobTitleB2B || data.jobTitle
              : data.jobTitleB2C || data.jobTitle)) && (
              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">
                  {isB2BG
                    ? formatTitleCase(data.jobTitleB2B || data.jobTitle || '')
                    : formatTitleCase(data.jobTitleB2C || data.jobTitle || '')}
                </span>
              </div>
            )}
            
            {/* Display Decision Authority based on business type */}
            {((isB2BG && (data.decisionAuthorityB2B || data.b2bDecisionAuthority)) || 
              (!isB2BG && (data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority))) && (
              <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="font-medium">
                  {isB2BG 
                    ? formatTitleCase(data.decisionAuthorityB2B || data.b2bDecisionAuthority || '') 
                    : formatTitleCase(data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority || '')}
                </span>
              </div>
            )}
            
            <div className="flex flex-wrap gap-y-1">
              {typeof data.industryType === 'string' && data.industryType && (
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{formatTitleCase(data.industryType)}</span>
                </div>
              )}

              {typeof data.industry === 'string' && data.industry && !data.industryType && (
                <div className="flex items-center text-sm text-gray-700 dark:text-gray-300 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>{formatTitleCase(data.industry)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Decision authority with badge */}
        {((isB2BG && (data.decisionAuthorityB2B || data.b2bDecisionAuthority)) || 
          (!isB2BG && (data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority))) && (
          <div className="mt-4 flex">
            <span className={clsx(
              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
              (isB2BG 
                ? (data.decisionAuthorityB2B || data.b2bDecisionAuthority || '')
                : (data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority || '')
              ).includes("Primary") || 
              (isB2BG
                ? (data.decisionAuthorityB2B || data.b2bDecisionAuthority || '')
                : (data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority || '')
              ).includes("Final")
                ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100" 
                : "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100"
            )}>
              {isB2BG
                ? formatTitleCase(data.decisionAuthorityB2B || data.b2bDecisionAuthority || '')
                : formatTitleCase(data.decisionAuthorityB2C || data.b2cDecisionAuthority || data.decisionAuthority || '')}
            </span>
          </div>
        )}
        
        {/* Responsibility area */}
        {((isB2BG && (data.responsibilitiesB2B || data.responsibilities)) || 
          (!isB2BG && (data.responsibilitiesB2C || data.responsibilities))) && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              <ClipboardIcon size="sm" tint="muted" margin="xs" />
              {isB2BG ? 'Key Responsibilities' : 'Daily Activities'}
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isB2BG
                ? (data.responsibilitiesB2B || data.responsibilities)
                : (data.responsibilitiesB2C || data.responsibilities)}
            </p>
          </div>
        )}
      </div>
    )
  }
  
  // Demographics preview
  if (stepId === 'demographics') {
    return (
      <div className="persona-preview-demographics p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Demographics Profile
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Age and Gender Group */}
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
            <div className="flex flex-col space-y-2">
              {data.ageRange && (
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300 mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Age Range</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.ageRange}</p>
                  </div>
                </div>
              )}
              
              {data.gender && (
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-300 mr-2">
                    <UserIcon size="sm" tint="purple" />
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Gender</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatTitleCase(data.gender || '')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Income and Education Group */}
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
            <div className="flex flex-col space-y-2">
              {data.income && (
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-green-600 dark:text-green-300 mr-2">
                    <BudgetCircleIcon size="sm" tint="success" /> 
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Income</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.income}</p>
                  </div>
                </div>
              )}
              
              {data.education && (
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-300 mr-2">
                    <GraduationCapIcon size="sm" tint="primary" /> 
                  </div>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Education</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.education}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Location and Family Status */}
          <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
            {data.location && (
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center text-red-600 dark:text-red-300 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Location</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.location}</p>
                </div>
              </div>
            )}
            
            {data.familyStatus && (
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center text-purple-600 dark:text-purple-300 mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Family Status</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.familyStatus}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
  
  // Psychographics preview
  if (stepId === 'psychographics') {
    // Determine business type context
    // `||`, NOT `??`: `includes()` returns the boolean `false` (not nullish)
    // when the substring is absent, so the original `?? ` short-circuited on
    // the first `false` and never evaluated the `b2g` check — making it dead
    // whenever businessType was defined-but-not-"b2b". `||` correctly ORs both
    // checks; the trailing `|| false` coerces the businessType-missing
    // (`undefined`) case to a plain boolean.
    const isB2BG = data.businessType?.toLowerCase().includes('b2b') || data.businessType?.toLowerCase().includes('b2g') || false
    // We don't need isB2C as we use !isB2BG for B2C logic
    
    return (
      <div className="persona-preview-psychographics p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          Psychographic Profile
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Values & Interests Panel */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900 dark:to-purple-900 p-4 rounded-md border border-indigo-100 dark:border-indigo-800">
            <h5 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2 flex items-center">
              <HeartIcon size="sm" margin="sm" />
              Values & Interests
            </h5>
            <div className="space-y-2">
              {/* Values based on business type */}
              {isB2BG && data.valuesB2B && Array.isArray(data.valuesB2B) && data.valuesB2B.length > 0 && (
                <div>
                  <span className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">Professional Values:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.valuesB2B.map((value, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 text-xs rounded-full">
                        {typeof value === 'string' ? value : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {!isB2BG && data.valuesB2C && Array.isArray(data.valuesB2C) && data.valuesB2C.length > 0 && (
                <div>
                  <span className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">Personal Values:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.valuesB2C.map((value, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 text-xs rounded-full">
                        {typeof value === 'string' ? value : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Show generic values if type-specific values aren't present */}
              {data.values && Array.isArray(data.values) && data.values.length > 0 && (
                <div>
                  <span className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">Values:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.values.map((value, i) => (
                      <span key={i} className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 text-xs rounded-full">
                        {typeof value === 'string' ? value : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Interests */}
              {data.interests && Array.isArray(data.interests) && data.interests.length > 0 && (
                <div>
                  <span className="text-xs text-indigo-600 dark:text-indigo-300 font-medium">Interests:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {data.interests.map((interest, i) => (
                      <span key={i} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 text-xs rounded-full">
                        {typeof interest === 'string' ? interest : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Goals & Challenges Panel */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/50 dark:to-cyan-900/50 p-4 rounded-md border border-blue-100 dark:border-blue-800">
            <h5 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-6 flex items-center">
              <OptimizationSparkleIcon size="sm" tint="blue" margin="sm" />
              Goals & Challenges
            </h5>

            <div className="space-y-8">
              {/* Goals Section */}
              {(() => {
                // Prefer specific business type goals, fall back to generic goals
                const goals = isB2BG ? data.goalsB2B ?? data.goals : data.goalsB2C ?? data.goals
                const goalType = isB2BG ? 'Professional' : 'Personal'
                
                if (goals) {
                  return (
                    <section>
                      <div className="flex items-center mb-3">
                        <GoalsIcon size="sm" tint="teal" />
                        <h6 className="text-sm font-medium text-cyan-800 dark:text-cyan-200 ml-1">
                          {goalType} Goals
                        </h6>
                      </div>
                      <div className="pl-4 border-l-2 border-cyan-200 dark:border-cyan-700">
                        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                          {truncateText(goals, PSYCHOGRAPHICS_MAX_LENGTH)}
                        </p>
                      </div>
                    </section>
                  )
                }
                return null
              })()}

              {/* Challenges Section */}
              {(() => {
                // Prefer specific business type challenges, fall back to generic challenges
                const challenges = isB2BG ? data.challengesB2B ?? data.challenges : data.challengesB2C ?? data.challenges
                const challengeType = isB2BG ? 'Professional' : 'Personal'
                
                if (challenges) {
                  return (
                    <section>
                      <div className="flex items-center mb-3">
                        <ChallengesIcon size="sm" tint="warning" />
                        <h6 className="text-sm font-medium text-orange-800 dark:text-orange-200 ml-1">
                          {challengeType} Challenges
                        </h6>
                      </div>
                      <div className="pl-4 border-l-2 border-orange-200 dark:border-orange-400 border-opacity-90 dark:border-opacity-70">
                        <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                          {truncateText(challenges, PSYCHOGRAPHICS_MAX_LENGTH)}
                        </p>
                      </div>
                    </section>
                  )
                }
                return null
              })()}
              
              {/* Pain Points Section */}
              {data.painPoints && (
                <section>
                  <div className="flex items-center mb-3">
                    <FeedbackChatBubbleIcon size="sm" tint="danger" />
                    <h6 className="text-sm font-medium text-red-800 dark:text-red-200 ml-1">
                      Pain Points
                    </h6>
                  </div>
                  <div className="pl-4 border-l-2 border-red-300 dark:border-red-500 border-opacity-80 dark:border-opacity-70">
                    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {truncateText(data.painPoints, PSYCHOGRAPHICS_MAX_LENGTH)}
                    </p>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  // Digital footprint preview
  if (stepId === 'digital-footprint' || stepId === 'digital-behavior' || stepId === 'digital') {
    return (
      <div className="persona-preview-digital p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          Digital Footprint
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Platforms Panel */}
          {data.platforms && Array.isArray(data.platforms) && data.platforms.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Platforms & Social Media
              </h5>
              <div className="flex flex-wrap gap-1">
                {data.platforms.map((platform, i) => {
                  const platformName = typeof platform === 'string' ? platform : ''
                  return (
                    <span key={i} className="inline-flex items-center px-3 py-1 bg-white dark:bg-gray-700 border border-blue-100 dark:border-blue-700 text-blue-700 dark:text-blue-200 rounded-full text-xs shadow-sm">
                      <span className="mr-1 h-4 w-4 flex items-center justify-center">{getPlatformIcon(platformName)}</span>
                      {platformName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
          
          {/* Devices Panel */}
          {data.devices && Array.isArray(data.devices) && data.devices.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900 rounded-md p-3 border border-green-100 dark:border-green-800 h-full">
              <h5 className="text-sm font-medium text-green-700 dark:text-green-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Device Usage
              </h5>
              <div className="flex flex-wrap gap-2">
                {data.devices.map((device, i) => {
                  const deviceName = typeof device === 'string' ? device : ''
                  return (
                    <span key={i} className="inline-flex items-center px-3 py-1 bg-white dark:bg-gray-700 border border-green-100 dark:border-green-700 text-green-700 dark:text-green-200 rounded-full text-xs shadow-sm">
                      <span className="mr-1">{getDeviceEmoji(deviceName)}</span>
                      {deviceName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
          
          {/* Content Consumption */}
          {data.contentTypes && Array.isArray(data.contentTypes) && data.contentTypes.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900 rounded-md p-3 border border-purple-100 dark:border-purple-800 h-full">
              <h5 className="text-sm font-medium text-purple-700 dark:text-purple-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Content Consumption
              </h5>
              <div className="flex flex-wrap gap-2">
                {data.contentTypes.map((contentType, i) => {
                  const contentTypeName = typeof contentType === 'string' ? contentType : ''
                  return (
                    <span key={i} className="inline-flex items-center px-2 py-1 bg-white dark:bg-gray-700 border border-purple-100 dark:border-purple-700 text-purple-700 dark:text-purple-200 rounded-full text-xs shadow-sm">
                      <span className="mr-1">{getContentEmoji(contentTypeName)}</span>
                      {contentTypeName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
          
          {/* Marketing channels
          {data.marketingChannels && Array.isArray(data.marketingChannels) && data.marketingChannels.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900 rounded-md p-3 border border-amber-100 dark:border-amber-800 h-full">
              <h5 className="text-sm font-medium text-amber-700 dark:text-amber-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
                Marketing Channels
              </h5>
              <div className="flex flex-wrap gap-2">
                {data.marketingChannels.map((channel, i) => {
                  const channelName = typeof channel === 'string' ? channel : '';
                  return (
                    <span key={i} className="inline-flex items-center px-3 py-1 bg-white dark:bg-gray-700 border border-amber-100 dark:border-amber-700 text-amber-700 dark:text-amber-200 rounded-full text-xs shadow-sm">
                      <span className="mr-1">{getChannelEmoji(channelName)}</span>
                      {channelName}
                    </span>
                  );
                })}
              </div>
            </div>
          )} */}

          {/* Digital Influence */}
          {data.digitalInfluence && (
            <div className="bg-teal-50 dark:bg-teal-900 rounded-md p-3 border border-teal-100 dark:border-teal-800 h-full">
              <h5 className="text-sm font-medium text-teal-700 dark:text-teal-200 mb-2 flex items-center">
                <StarIcon size="sm" margin="xs" />
                Digital Influence
              </h5>
              <div className="p-2.5 bg-teal-50/90 dark:bg-teal-800/90 rounded border border-teal-200/80 dark:border-teal-700/80">
                <div className="flex items-center">
                  <span className="mr-2 h-5 w-5 flex items-center justify-center">{getDigitalInfluenceIcon(data.digitalInfluence)}</span>
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {data.digitalInfluence === 'Content Creator/Influencer' 
                      ? 'Influencer / Content Creator' 
                      : data.digitalInfluence === 'Minimal Digital Presence' 
                        ? 'Minimal Presence' 
                        : data.digitalInfluence}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Online Behavior - Full width */}
          {data.onlineBehavior && (
            <div className="col-span-1 sm:col-span-2 bg-emerald-100 dark:bg-emerald-900 rounded-md p-3 border border-emerald-200 dark:border-emerald-800 h-full mt-3">
              <h5 className="text-sm font-medium text-emerald-700 dark:text-emerald-200 mb-2 flex items-center">
                <OptimizationSparkleIcon size="sm" margin="xs" />
                Online Behavior
              </h5>
              <div className="p-2.5 bg-emerald-50/90 dark:bg-emerald-800/90 rounded border border-emerald-200/80 dark:border-emerald-700/80">
                <p className="text-sm text-gray-700 dark:text-gray-200">{truncateText(data.onlineBehavior, ONLINE_BEHAVIOR_MAX_LENGTH)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Buying behavior preview
  if (stepId === 'buying-behavior' || stepId === 'buying-patterns' || stepId === 'behavior') {
    // Determine business type context
    // `||`, NOT `??`: `includes()` returns the boolean `false` (not nullish)
    // when the substring is absent, so the original `?? ` short-circuited on
    // the first `false` and never evaluated the `b2g` check — making it dead
    // whenever businessType was defined-but-not-"b2b". `||` correctly ORs both
    // checks; the trailing `|| false` coerces the businessType-missing
    // (`undefined`) case to a plain boolean.
    const isB2BG = data.businessType?.toLowerCase().includes('b2b') || data.businessType?.toLowerCase().includes('b2g') || false
    // We don't need isB2C as we use !isB2BG for B2C logic
    
    return (
      <div className="persona-preview-buying p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Buying Behavior
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Research Methods */}
          {data.researchMethods && Array.isArray(data.researchMethods) && data.researchMethods.length > 0 && (
            <div className="bg-indigo-50 dark:bg-indigo-900 rounded-md p-3 border border-indigo-100 dark:border-indigo-800 h-full">
              <h5 className="text-sm font-medium text-indigo-700 dark:text-indigo-200 mb-2 flex items-center">
                <SearchIcon size="sm" margin="sm" />
                Research Methods
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {data.researchMethods.map((method, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-700 border border-indigo-100 dark:border-indigo-700 text-indigo-700 dark:text-indigo-200 rounded-full text-xs shadow-sm">
                    {typeof method === 'string' ? method : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Decision Criteria */}
          {(() => {
            // Handle both B2B and B2C versions with proper type checking
            // Prefer specific business type criteria, fall back to generic criteria
            const criteriaB2B = data.decisionCriteriaB2B ?? data.b2bDecisionCriteria
            const criteriaB2C = data.decisionCriteriaB2C ?? data.b2cDecisionCriteria
            const criteria = isB2BG ? criteriaB2B ?? data.decisionCriteria : criteriaB2C ?? data.decisionCriteria
            
            if (criteria && Array.isArray(criteria) && criteria.length > 0) {
              return (
                <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
                  <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                    <ClipboardIcon size="sm" margin="sm" />
                    Decision Criteria
                  </h5>
                  <div className="flex flex-wrap gap-1.5">
                    {criteria.map((item, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-700 border border-blue-100 dark:border-blue-700 text-blue-700 dark:text-blue-200 rounded-full text-xs shadow-sm">
                        {typeof item === 'string' ? item : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )
            }
            return null
          })()}
          
          {/* Purchase Behavior - Combined card for Purchase Frequency and Budget Consciousness */}
          {(data.purchaseFrequency ?? data.budgetConsciousness) && (
            <div className="bg-amber-50 dark:bg-amber-900 rounded-md p-3 border border-amber-100 dark:border-amber-800 h-full">
              <h5 className="text-sm font-medium text-amber-700 dark:text-amber-200 mb-3 flex items-center">
                <ShoppingCartIcon size="sm" margin="sm" />
                Purchase Behavior
              </h5>
              
              <div className="space-y-3">
                {/* Purchase Frequency */}
                {data.purchaseFrequency && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-800 rounded-full mr-3">
                      <ClockIcon size="sm" tint="warning" />
                    </div>
                    <div>
                      <span className="text-xs text-amber-600 dark:text-amber-300">Frequency</span>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.purchaseFrequency}</p>
                    </div>
                  </div>
                )}
                
                {/* Budget Consciousness */}
                {data.budgetConsciousness && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-800 rounded-full mr-3">
                      {(() => {
                        // Select appropriate icon based on budget consciousness level
                        const budget = data.budgetConsciousness.toLowerCase()
                        if (budget.includes('extremely')) {
                          return (
                            <SalePromoIcon size="sm" tint="warning" />
                          )
                        }
                        if (budget.includes('very')) {
                          return (
                            <ShoppingBasketIcon size="sm" tint="warning" />
                          )
                        }
                        if (budget.includes('somewhat')) {
                          return (
                            <ScalesOfJusticeIcon size="sm" tint="warning" />
                          )
                        }
                        if (budget.includes('slightly')) {
                          return (
                            <GiftIcon size="sm" tint="warning" />
                          )
                        }
                        if (budget.includes('not')) {
                          return (
                            <VIPIcon size="sm" tint="warning" />
                          )
                        }
                        return (
                          <svg className="h-4 w-4 text-amber-700 dark:text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )
                      })()}
                    </div>
                    <div>
                      <span className="text-xs text-amber-600 dark:text-amber-300">Budget</span>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {(() => {
                          const budget = data.budgetConsciousness.toLowerCase()
                          if (budget.includes('extremely')) return "Very price sensitive"
                          if (budget.includes('very')) return "Price sensitive"
                          if (budget.includes('somewhat')) return "Price aware"
                          if (budget.includes('slightly')) return "Value oriented"
                          if (budget.includes('not')) return "Price insensitive"
                          return data.budgetConsciousness
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Communication Channels */}
          {data.communicationChannels && Array.isArray(data.communicationChannels) && data.communicationChannels.length > 0 && (
            <div className="bg-violet-50 dark:bg-violet-900 rounded-md p-3 border border-violet-100 dark:border-violet-800 h-full">
              <h5 className="text-sm font-medium text-violet-700 dark:text-violet-200 mb-2 flex items-center">
                <CommunicationIcon size="sm" margin="sm" />
                Preferred Communication
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {data.communicationChannels.map((channel, i) => {
                  const channelName = typeof channel === 'string' ? channel : ''
                  return (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-700 border border-violet-100 dark:border-violet-700 text-violet-700 dark:text-violet-200 rounded-full text-xs shadow-sm">
                      <span className="mr-1 h-4 w-4 flex items-center justify-center">{getChannelIcon(channelName)}</span>
                      {channelName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Brand Loyalty */}
        {data.brandLoyalty && (
          <div className="bg-rose-100 dark:bg-rose-900 rounded-md p-3 border border-rose-200 dark:border-rose-800 h-full mt-3">
            <h5 className="text-sm font-medium text-rose-700 dark:text-rose-200 mb-2 flex items-center">
              <BrandLoyaltyHeartIcon size="sm" margin="sm" />
              Brand Loyalty Factors
            </h5>
            <div className="p-2.5 bg-rose-50/90 dark:bg-rose-800/90 rounded border border-rose-200/80 dark:border-rose-700/80">
              <p className="text-sm text-gray-700 dark:text-gray-200">{truncateText(data.brandLoyalty, PSYCHOGRAPHICS_MAX_LENGTH)}</p>
            </div>
          </div>
        )}
      </div>
    )
  }
  
  // Targeting preview
  if (stepId === 'targeting') {
    return (
      <div className="persona-preview-targeting p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Ad Platform Targeting
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Meta Targeting */}
          {data.metaTargeting && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 rounded-md border border-blue-100 dark:border-blue-800 p-3">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <FacebookIcon size="sm" margin="sm" />
                Meta/Facebook Targeting
              </h5>
              <p className="text-sm text-blue-800 dark:text-blue-200 bg-white dark:bg-blue-500 bg-opacity-60 dark:bg-opacity-40 p-2 rounded border border-blue-100 dark:border-blue-500">
                {truncateText(data.metaTargeting, TARGETING_MAX_LENGTH)}
              </p>
            </div>
          )}
          
          {/* Google Targeting */}
          {data.googleTargeting && (
            <div className="bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900 dark:to-orange-900 rounded-md border border-red-100 dark:border-red-800 p-3">
              <h5 className="text-sm font-medium text-red-700 dark:text-red-200 mb-2 flex items-center">
                <GoogleIcon size="xs" margin="sm" />
                Google Ads Targeting
              </h5>
              <div className="p-2.5 bg-red-50 dark:bg-red-400 rounded border bg-opacity-90 dark:bg-opacity-30 border-red-200/80 dark:border-red-400/70">
                <p className="text-sm text-red-900 dark:text-red-100">{truncateText(data.googleTargeting, TARGETING_MAX_LENGTH)}</p>
              </div>
            </div>
          )}
          
          {/* LinkedIn Targeting */}
          {data.linkedinTargeting && (
            <div className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-900 dark:to-sky-900 rounded-md border border-blue-100 dark:border-blue-800 p-3">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <LinkedInIcon size="sm" margin="sm" />
                LinkedIn Targeting
              </h5>
              <p className="text-sm text-blue-800 dark:text-blue-200 bg-white dark:bg-sky-500 bg-opacity-60 dark:bg-opacity-30 p-2 rounded border border-blue-100 dark:border-sky-600">
                {truncateText(data.linkedinTargeting, TARGETING_MAX_LENGTH)}
              </p>
            </div>
          )}
          
          {/* TikTok Targeting */}
          {data.tiktokTargeting && (
            <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900 dark:to-gray-900 rounded-md border border-slate-100 dark:border-slate-800 p-3">
              <h5 className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 flex items-center">
                <TikTokIcon size="sm" margin="sm" />
                TikTok Targeting
              </h5>
              <p className="text-sm text-slate-800 dark:text-slate-200 bg-white dark:bg-gray-500 bg-opacity-60 dark:bg-opacity-30 p-2 rounded border border-slate-200 dark:border-slate-600">
                {truncateText(data.tiktokTargeting, TARGETING_MAX_LENGTH)}
              </p>
            </div>
          )}
          
          {/* Additional Targeting Recommendations */}
          {data.targetingRecommendations && (
            <div className="col-span-1 sm:col-span-2 bg-gradient-to-br from-purple-50 to-fuchsia-50 dark:from-purple-900 dark:to-fuchsia-900 rounded-md border border-purple-100 dark:border-purple-800 p-3">
              <h5 className="text-sm font-medium text-purple-700 dark:text-purple-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                Additional Targeting Recommendations
              </h5>
              <p className="text-sm text-purple-800 dark:text-purple-200 bg-white dark:bg-gray-700 bg-opacity-60 dark:bg-opacity-30 p-2 rounded">
                {truncateText(data.targetingRecommendations, TARGETING_MAX_LENGTH)}
              </p>
            </div>
          )}
          
          {/* Custom Audience Strategy */}
          {data.customAudiences && (
            <div className="col-span-1 sm:col-span-2 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900 dark:to-indigo-900 rounded-md border border-violet-100 dark:border-violet-800 p-3 mt-3">
              <h5 className="text-sm font-medium text-violet-800 dark:text-violet-200 mb-2 flex items-center">
                <UserTargetingIcon size="sm" tint="violet" margin="sm" />
                Custom Audience Strategy
              </h5>
              <div className="p-2.5 bg-white dark:bg-violet-500 bg-opacity-60 dark:bg-opacity-30 rounded border border-violet-100 dark:border-violet-600">
                <p className="text-sm text-violet-800 dark:text-violet-100">{truncateText(data.customAudiences, PSYCHOGRAPHICS_MAX_LENGTH)}</p>
              </div>
            </div>
          )}
          
          {/* Lookalike Strategy */}
          {data.lookalikeStrategy && (
            <div className="col-span-1 sm:col-span-2 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900 dark:to-blue-900 rounded-md border border-cyan-100 dark:border-cyan-800 p-3 mt-3">
              <h5 className="text-sm font-medium text-cyan-700 dark:text-cyan-200 mb-2 flex items-center">
                <LookalikeTargetingIcon size="sm" tint="teal" margin="sm" />  
                Lookalike Audience Strategy
              </h5>
              <p className="text-sm text-cyan-800 dark:text-cyan-100 bg-white dark:bg-cyan-600 bg-opacity-60 dark:bg-opacity-30 p-2 rounded border border-cyan-100 dark:border-cyan-700">
                {truncateText(data.lookalikeStrategy, PSYCHOGRAPHICS_MAX_LENGTH)}
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Optimization and Testing preview
  if (stepId === 'optimization-testing' || stepId === 'optimization') {
    
    return (
      <div className="persona-preview-optimization p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Optimization & Testing
        </h4>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* KPIs */}
          {data.kpis && Array.isArray(data.kpis) && data.kpis.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <KPIsIcon size="sm" margin="sm" />
                Key Performance Indicators
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {data.kpis.map((kpi, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-700 border border-blue-100 dark:border-blue-700 text-blue-700 dark:text-blue-200 rounded-full text-xs shadow-sm">
                    {typeof kpi === 'string' ? kpi : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Test Variables */}
          {data.testVariables && Array.isArray(data.testVariables) && data.testVariables.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900 rounded-md p-3 border border-purple-100 dark:border-purple-800 h-full">
              <h5 className="text-sm font-medium text-purple-700 dark:text-purple-200 mb-2 flex items-center">
                <TestingFlaskIcon size="sm" margin="sm" />
                Test Variables
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {data.testVariables.map((variable, i) => (
                  <span key={i} className="inline-flex items-center px-2 py-0.5 bg-white dark:bg-gray-700 border border-purple-100 dark:border-purple-700 text-purple-700 dark:text-purple-200 rounded-full text-xs shadow-sm">
                    {typeof variable === 'string' ? variable : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Budget Allocation */}
          {data.budgetAllocation && (
            <div className="bg-green-50 dark:bg-green-900 rounded-md p-3 border border-green-100 dark:border-green-800 h-full">
              <h5 className="text-sm font-medium text-green-700 dark:text-green-200 mb-2 flex items-center">
                <BudgetCircleIcon size="sm" margin="sm" />
                Budget Allocation
              </h5>
              <div className="p-2.5 bg-white dark:bg-green-700 bg-opacity-60 dark:bg-opacity-25 rounded border border-green-100 dark:border-green-700 border-opacity-90 dark:border-opacity-50">
                <p className="text-sm text-green-800 dark:text-green-100">{data.budgetAllocation}</p>
              </div>
            </div>
          )}
          
          {/* Success Metrics */}
          {data.successMetrics && (
            <div className="bg-cyan-50 dark:bg-cyan-900 rounded-md p-3 border border-cyan-100 dark:border-cyan-800 h-full">
              <h5 className="text-sm font-medium text-cyan-700 dark:text-cyan-200 mb-2 flex items-center">
                <SuccessIcon size="sm" margin="sm" />
                Success Metrics
              </h5>
              <div className="p-2.5 bg-white dark:bg-cyan-700 bg-opacity-60 dark:bg-opacity-25 rounded border border-cyan-100 dark:border-cyan-700 border-opacity-90 dark:border-opacity-50">
                <p className="text-sm text-cyan-800 dark:text-cyan-100">{data.successMetrics}</p>
              </div>
            </div>
          )}
          
          {/* Optimization Strategies */}
          {data.optimizationStrategies && (
            <div className="bg-indigo-50 dark:bg-indigo-900 rounded-md p-3 border border-indigo-100 dark:border-indigo-800 h-full">
              <h5 className="text-sm font-medium text-indigo-700 dark:text-indigo-200 mb-2 flex items-center">
                <OptimizationFlashlightIcon size="sm" margin="sm" />
                Optimization Strategies
              </h5>
              <div className="p-2.5 bg-white dark:bg-indigo-600 bg-opacity-60 dark:bg-opacity-25 rounded border border-indigo-100 dark:border-indigo-600 border-opacity-90 dark:border-opacity-50">
                <p className="text-sm text-indigo-800 dark:text-indigo-100">{data.optimizationStrategies}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Default empty preview
  return (
    <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex flex-col items-center justify-center py-8">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Complete the form fields to see a preview of your buyer persona.<br />
          The preview will update as you enter information.
        </p>
      </div>
    </div>
  )
} 